import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, CanDeactivate } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { UserService } from '../services';
import { take } from 'rxjs/operators';
import { SplashDialogService } from 'app/shared/dialogs/splash';


@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {

    constructor(private splashSvc: SplashDialogService, private userSvc: UserService) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        // At a minimum, only allow access if we have a valid authtoken stored for the current user.
        const user = this.userSvc.User;
        let routeOk = !!user?.AuthToken;
        let matchingRoles: boolean | null = null;
        let matchingCurrentRole: boolean | null = null;

        // If there is optional data present, particularly the 'roles' setting, we must also check to ensure that the
        // current user has a role that is in the provided list of necessary roles to use the route.
        const key = 'roles';
        
        if (routeOk && route.data != null && route.data[key] != null) {

            // Get list of roles attached to route
            const neededRoles = (route.data[key] || []) as string[];

            // If no list exist or list is empty, then any user can view page, so return true.
            if (!neededRoles || neededRoles == [])
                routeOk = true;
            else {
                matchingRoles = neededRoles.some((nr) => !!user!.Roles?.find((ur) => ur.RoleName === nr));
                matchingCurrentRole = neededRoles.some((nr) => user!.CurrentRoleInfo?.RoleName! === nr);

                if (!matchingRoles || !matchingCurrentRole)
                    // Cannot find any of the needed roles within the list of user owned roles, or the user's
                    // current role does not match any of the needed roles. Both end in failure.
                    routeOk = false;
            }
        }

        // If there are no matching roles and the route needs roles, then user is not authorized to view this page.
        if (!routeOk && route.data[key] != null) {

            // If the user has multiple roles and their current role is not set to any of the matching roles, 
            // then suggest switching roles. Otherwise, reject user.
            if (user!.IsMultiRole && matchingRoles && !matchingCurrentRole) 
                this.splashSvc.SnackbarError('Your current role is not authorized to view the requested page. Try changing roles using the role selector to view the page.');
            else {
                this.splashSvc.SnackbarError('You are not authorized to view the requested page.');}
        }

        return routeOk;
    }
}

export interface UnsavedChanges {
    CanDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
    ChangeTrackingActive: boolean;
    SetupTracking: () => void;
    TakeDownTracking: () => void;
}

@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChanges> {
    constructor(private unsavedSvc: UnsavedChangesService) {}

    canDeactivate(target: UnsavedChanges): Observable<boolean> | Promise<boolean> | boolean {
        if (!this.unsavedSvc.HasTracker()) {
            return true;
        }
        
        return target.CanDeactivate ? target.CanDeactivate() : true;
    }
}

@Injectable()
export class UnsavedChangesService {
    private _nativeListener: EventListenerOrEventListenerObject | null;
    private _tracker: null | (() => Observable<boolean> | Promise<boolean> | boolean) = null;

    // tslint:disable-next-line: no-any
    private _nativeChecker = (checker: any, event: any) => {
        const isClean = checker != null ? checker(true) : false;

        if (!isClean) {
            const msg = 'There may be unsaved changes on this page. Do you wish to continue leaving the page?';

            // tslint:disable-next-line: deprecation
            event = event || window.event;

            if (event) {
                event.returnValue = msg;
            }

            return msg;
        } else {
            return;
        }
        // tslint:disable-next-line: semicolon
    };

    constructor() {
        this._nativeListener = null;
    }

    // #region Clear Tracking Method

    ClearTracking(): void {
        if (this._nativeListener) {
            window.removeEventListener('beforeunload', this._nativeListener);
            this._nativeListener = null;
        }

        this._tracker = null;
    }

    // #endregion

    // #region Check Tracker Method

    CheckTracker(): Observable<boolean> {
        const self = this;

        if (self._tracker != null) {
            const result = self._tracker();
            let obsResult: Observable<boolean>;

            if (result instanceof Promise) {
                obsResult = from(result);
            } else if (!(result instanceof Observable)) {
                obsResult = of<boolean>(result).pipe(take(1));
            } else {
                obsResult = result as Observable<boolean>;
            }

            return obsResult.pipe(take(1));
        } else {
            return of<boolean>(true).pipe(take(1));
        }
    }

    // #endregion

    // #region Has Tracker

    HasTracker(): boolean {
        return this._tracker != null;
    }

    // #endregion

    // #region Set Tracking Method

    SetTracking(tracker: () => Observable<boolean> | Promise<boolean> | boolean): void {
        const self = this;

        if (self._nativeListener) {
            self.ClearTracking();
        }

        self._nativeListener = self._nativeChecker.bind(window, tracker);

        window.addEventListener('beforeunload', self._nativeListener);

        this._tracker = tracker;
    }

    // #endregion
}